/**
 * crewQueries.ts — Supabase query helpers for the Crew App.
 *
 * Every row returned here maps 1:1 to a view or table created by the
 * migration `supabase/migrations/20260424_crew_leaderboard.sql`.
 */
import { supabase } from "../../lib/supabase";
import {
  addDaysISO,
  mondayOf,
  reviewCreditDays,
  type ReviewCreditRow,
  type SpiffActivityRow,
} from "../../lib/weeklySpiffs";
import { fmtUSD } from "../../lib/analytics";
import type { ApprovalSpiff } from "../../lib/spiffApproval";
import { fetchCalendarBuilds } from "../../lib/buildRevenue";

export type Timeframe = "week" | "month" | "ytd";

/** One row of the `crew_leaderboard` view (points + $ SPIFFs, all periods). */
export interface CrewLeaderboardRow {
  user_id: string;
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
  /** On leave (college, etc). Kept in the view so split credit shows; hidden from goals and ranked only when they score. */
  on_leave: boolean | null;
  points_week: number;
  points_month: number;
  points_ytd: number;
  apps_week: number;
  claims_week: number;
  builds_scheduled_week: number;
  builds_week: number;
  reviews_week: number;
  revenue_week_cents: number;
  apps_month: number;
  claims_month: number;
  builds_scheduled_month: number;
  builds_month: number;
  reviews_month: number;
  revenue_month_cents: number;
  apps_ytd: number;
  claims_ytd: number;
  builds_scheduled_ytd: number;
  builds_ytd: number;
  reviews_ytd: number;
  revenue_ytd_cents: number;
  spiffs_week_cents: number;
  spiffs_month_cents: number;
  spiffs_ytd_cents: number;
  // Competition-year boundary (1st Friday of December reset — Christmas-party crowning).
  // Same value on every row; the view emits it as a convenience so the UI doesn't
  // need a second round-trip.
  year_start: string;
  year_end: string;
}

/** One row of the live activity feed — activity log joined to the author. */
export interface CrewActivityEvent {
  id: string;
  user_id: string;
  user_name: string;
  kind: "app" | "claim" | "build" | "build_scheduled" | "revenue" | "review";
  value: number;
  occurred_at: string;
  note: string | null;
}

/** A manager-awarded cash SPIFF. */
export interface SpiffEvent {
  id: string;
  user_id: string;
  user_name: string;
  name: string;
  amount_cents: number;
  job_address: string | null;
  claimed_at: string;
}

/** Goal row (weekly/monthly/yearly targets). */
export interface CrewGoal {
  id: string;
  scope: "personal" | "company";
  user_id: string | null;
  period: "week" | "month" | "year";
  metric:
    | "apps"
    | "claims"
    | "builds"
    | "builds_scheduled"
    | "revenue"
    | "points"
    | "dollars";
  target: number;
  period_start: string;
}

// ───────────────────────── leaderboard ─────────────────────────
export async function fetchLeaderboard(): Promise<CrewLeaderboardRow[]> {
  const { data, error } = await supabase
    .from("crew_leaderboard")
    .select("*");
  if (error) throw error;
  return (data ?? []) as CrewLeaderboardRow[];
}

/**
 * Fetch the current commitment-streak count for every active rep. Returns a
 * map of user_id → streak_weeks. Used by the Leaderboard board view + wall to
 * surface the 🔥 N chip after a rep's name.
 *
 * Sources from `team_roster.commitment_streak_weeks`, cached by the
 * `commitment_streak_recompute_weekly` cron (every Monday 5:01 UTC). Always
 * <= 7d stale, no real-time refresh needed.
 */
export async function fetchCommitmentStreaks(): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from("team_roster")
    .select("id, commitment_streak_weeks")
    .eq("is_active", true);
  if (error) throw error;
  const out: Record<string, number> = {};
  for (const r of (data ?? []) as Array<{ id: string; commitment_streak_weeks: number | null }>) {
    if (r.commitment_streak_weeks && r.commitment_streak_weeks > 0) out[r.id] = r.commitment_streak_weeks;
  }
  return out;
}

// ───────────────────────── activity feed ─────────────────────────
/**
 * Last N events across the crew, newest first. Uses the scoped view
 * `crew_activity_feed` so only ASM/TL/RM events show up on the rail
 * (everyone else is still in the underlying log — just not displayed).
 */
export async function fetchRecentActivity(limit = 12): Promise<CrewActivityEvent[]> {
  const { data, error } = await supabase
    .from("crew_activity_feed")
    .select("id, user_id, user_name, kind, value, occurred_at, note")
    .order("occurred_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id as string,
    user_id: r.user_id as string,
    user_name: (r.user_name as string | null) ?? "Unknown",
    kind: r.kind as CrewActivityEvent["kind"],
    value: (r.value as number) ?? 1,
    occurred_at: r.occurred_at as string,
    note: (r.note as string | null) ?? null,
  }));
}

/** Recent SPIFF claims (for the feed + race-lane ticker). */
export async function fetchRecentSpiffs(limit = 12): Promise<SpiffEvent[]> {
  const { data, error } = await supabase
    .from("crew_spiff_feed")
    .select("id, user_id, user_name, name, amount_cents, job_address, claimed_at")
    .order("claimed_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id as string,
    user_id: r.user_id as string,
    user_name: (r.user_name as string | null) ?? "Unknown",
    name: r.name as string,
    amount_cents: (r.amount_cents as number) ?? 0,
    job_address: (r.job_address as string | null) ?? null,
    claimed_at: r.claimed_at as string,
  }));
}

// Shout-out wall queries removed Oct 12, 2026 (wall retired; 2 posts in 90 days).

// ───────────────────────── goals ─────────────────────────
/** Fetch the current weekly/monthly goals for a user + the company. */
export async function fetchCurrentGoals(): Promise<CrewGoal[]> {
  const { data, error } = await supabase
    .from("crew_goals")
    .select("*")
    .order("period_start", { ascending: false });
  if (error) throw error;
  return (data ?? []) as CrewGoal[];
}

/**
 * Monday (YYYY-MM-DD) of the current week on the ET calendar — the same rule
 * as `mondayOf()` in lib/weeklySpiffs (Sunday still belongs to the week that
 * is ending). This is THE key every weekly goal row is written and read under.
 *
 * Sep 28, 2026: this used to do local-date arithmetic and then take
 * `toISOString().slice(0, 10)` — i.e. the UTC date of a local-time instant.
 * After 8 PM ET (00:00 UTC) that is TUESDAY's date, so every evening the crew
 * board and the home band fetched goals under a key nobody had written, got
 * nothing back, and fell back to the per-head defaults. Monday's goals looked
 * like they changed every night and came back every morning. Never rebuild
 * this from `new Date()` local math — go through the ET helpers.
 */
export function currentWeekStart(): string {
  return mondayOf();
}

/**
 * Fetch all weekly goals for the current Monday. Grouped by user_id (or
 * the special key "company" for scope=company).
 */
export type GoalMetricId =
  | "apps"
  | "claims"
  | "builds_scheduled"
  | "builds";

export interface WeeklyGoalsMap {
  /** `{ [userId|"company"]: { apps?: number; claims?: number; builds_scheduled?: number; builds?: number } }` */
  [owner: string]: Partial<Record<GoalMetricId, number>>;
}

const WEEKLY_GOAL_METRICS: GoalMetricId[] = [
  "apps",
  "claims",
  "builds_scheduled",
  "builds",
];

export async function fetchWeeklyGoalsMap(
  weekStart = currentWeekStart(),
): Promise<WeeklyGoalsMap> {
  const { data, error } = await supabase
    .from("crew_goals")
    .select("scope, user_id, metric, target")
    .eq("period", "week")
    .eq("period_start", weekStart)
    .in("metric", WEEKLY_GOAL_METRICS)
    // Oldest first, so if two rows ever shared a key the latest write would
    // win deterministically (crew_goals_one_row_per_key makes that impossible
    // now, but a read should never depend on undefined row order).
    .order("created_at", { ascending: true });
  if (error) throw error;
  const out: WeeklyGoalsMap = {};
  for (const r of data ?? []) {
    const key = (r.scope as string) === "company" ? "company" : (r.user_id as string);
    if (!out[key]) out[key] = {};
    (out[key] as Record<string, number>)[r.metric as string] = Number(r.target);
  }
  return out;
}

/**
 * Upsert a single weekly goal. Idempotent — one row per
 * (scope, user_id, period, period_start, metric), enforced by the
 * `crew_goals_one_row_per_key` constraint (NULLS NOT DISTINCT, so the company
 * row's NULL user_id conflicts like a value). target=0 removes the row (lets
 * managers clear a goal).
 *
 * Sep 28, 2026: this was delete-then-insert with no unique key behind it, so
 * two saves racing on the same metric could leave two rows and the board
 * would read whichever came back last. A real upsert can't.
 *
 * Decimals welcome: split-RM activity rows are 0.5, so a sensible per-week
 * goal can also be fractional (e.g. 1.5 scheduled builds).
 */
export async function setWeeklyGoal(input: {
  userId: string | null; // null = company-wide
  metric: GoalMetricId;
  target: number;
  weekStart?: string;
  createdBy: string;
}): Promise<void> {
  const ws = input.weekStart ?? currentWeekStart();
  const scope = input.userId ? "personal" : "company";

  if (input.target <= 0) {
    // Clearing a goal = removing its row.
    let del = supabase
      .from("crew_goals")
      .delete()
      .eq("scope", scope)
      .eq("period", "week")
      .eq("period_start", ws)
      .eq("metric", input.metric);
    del = input.userId ? del.eq("user_id", input.userId) : del.is("user_id", null);
    const { error } = await del;
    if (error) throw error;
    return;
  }

  const { error } = await supabase.from("crew_goals").upsert(
    {
      scope,
      user_id: input.userId,
      period: "week",
      metric: input.metric,
      target: input.target,
      period_start: ws,
      created_by: input.createdBy,
      // Refreshed on every write so it keeps meaning "last set at", exactly
      // what the old delete-then-insert produced.
      created_at: new Date().toISOString(),
    },
    { onConflict: "scope,user_id,period,period_start,metric" },
  );
  if (error) throw error;
}

// ───────────────────────── drilldown (wall stat → deal list) ─────────────────────────

/**
 * Activity kinds that can be drilled into from the Wall.
 * Maps 1:1 to the `kind` column in crew_activity_log.
 */
export type DrilldownKind =
  | "app"
  | "claim"
  | "build_scheduled"
  | "build"
  | "revenue";

/** Maps a wall column key to the underlying activity log kind. */
export const METRIC_TO_KIND: Record<
  "apps" | "claims" | "builds_scheduled" | "builds" | "revenue_cents",
  DrilldownKind
> = {
  apps: "app",
  claims: "claim",
  builds_scheduled: "build_scheduled",
  builds: "build",
  revenue_cents: "revenue",
};

/** Human label for each drilldown kind, used in the modal header. */
export const KIND_LABELS: Record<DrilldownKind, string> = {
  app: "Apps",
  claim: "Claims",
  build_scheduled: "Signed IA",
  build: "Builds",
  revenue: "Revenue",
};

/** One deal contributing to a rep's stat in the drilldown panel. */
export interface DrilldownDeal {
  activity_id: string;
  deal_id: string;
  kind: DrilldownKind;
  /** Points value (may be 0.5 for split deals). For revenue kind, value is in cents. */
  value: number;
  occurred_at: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  property_state: string | null;
  stage: string | null;
}

/**
 * Fetch crew_activity_log rows for a specific user + kind within a date range,
 * joined to deal details. Uses two queries to avoid FK naming ambiguity.
 */
export async function fetchDrilldownDeals(
  userId: string,
  kind: DrilldownKind,
  startISO: string,
  endISO: string,
): Promise<DrilldownDeal[]> {
  // Step 1: get activity rows
  const { data: actRows, error: actErr } = await supabase
    .from("crew_activity_log")
    .select("id, deal_id, kind, value, occurred_at")
    .eq("user_id", userId)
    .eq("kind", kind)
    .gte("occurred_at", startISO)
    .lt("occurred_at", endISO)
    .not("deal_id", "is", null)
    .order("occurred_at", { ascending: false });
  if (actErr) throw actErr;
  if (!actRows || actRows.length === 0) return [];

  // Step 2: fetch deal details for those IDs
  const dealIds = actRows.map((r) => r.deal_id as string);
  const { data: dealRows, error: dealErr } = await supabase
    .from("deals")
    .select("id, homeowner_name, property_street, property_city, property_state, stage")
    .in("id", dealIds);
  if (dealErr) throw dealErr;

  const dealMap = new Map(
    (dealRows ?? []).map((d) => [
      d.id as string,
      d as {
        id: string;
        homeowner_name: string | null;
        property_street: string | null;
        property_city: string | null;
        property_state: string | null;
        stage: string | null;
      },
    ]),
  );

  return actRows.map((r) => {
    const d = dealMap.get(r.deal_id as string);
    return {
      activity_id: r.id as string,
      deal_id: r.deal_id as string,
      kind: r.kind as DrilldownKind,
      value: Number(r.value ?? 0),
      occurred_at: r.occurred_at as string,
      homeowner_name: d?.homeowner_name ?? null,
      property_street: d?.property_street ?? null,
      property_city: d?.property_city ?? null,
      property_state: d?.property_state ?? null,
      stage: d?.stage ?? null,
    };
  });
}

// ───────────────────────── leaderboard wall (3rd tab) ─────────────────────────
/**
 * Time-range identifiers for the Leaderboard Wall. The wall computes
 * aggregates per user from `crew_daily_points` over a custom date window,
 * so the same view powers all 8 filters.
 */
export type WallRange =
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "rolling_30"
  | "last_ytd";

export const WALL_RANGE_LABELS: Record<WallRange, string> = {
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_quarter: "Last quarter",
  rolling_30: "Rolling 30",
  last_ytd: "Last YTD",
};

/** Aggregated stats for a single rep over the chosen date window. */
export interface WallStat {
  user_id: string;
  apps: number;
  claims: number;
  builds_scheduled: number;
  builds: number;
  revenue_cents: number;
}

/** Monday (00:00 local) of the week containing `d`. */
function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const day = out.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  out.setDate(out.getDate() + diff);
  return out;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1);
}

/**
 * Resolve a wall range to a half-open [start, end) window. End is exclusive
 * so date_trunc('day', occurred_at) < end is the right comparison.
 */
export function rangeFor(r: WallRange, now: Date = new Date()): {
  start: Date;
  end: Date;
  label: string;
} {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const label = WALL_RANGE_LABELS[r];
  switch (r) {
    case "this_week": {
      return { start: startOfWeek(now), end: tomorrow, label };
    }
    case "last_week": {
      const thisMon = startOfWeek(now);
      const lastMon = new Date(thisMon);
      lastMon.setDate(lastMon.getDate() - 7);
      return { start: lastMon, end: thisMon, label };
    }
    case "this_month": {
      return { start: startOfMonth(now), end: tomorrow, label };
    }
    case "last_month": {
      const thisM = startOfMonth(now);
      const lastM = new Date(thisM);
      lastM.setMonth(lastM.getMonth() - 1);
      return { start: lastM, end: thisM, label };
    }
    case "this_quarter": {
      return { start: startOfQuarter(now), end: tomorrow, label };
    }
    case "last_quarter": {
      const thisQ = startOfQuarter(now);
      const lastQ = new Date(thisQ);
      lastQ.setMonth(lastQ.getMonth() - 3);
      return { start: lastQ, end: thisQ, label };
    }
    case "rolling_30": {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { start, end: tomorrow, label };
    }
    case "last_ytd": {
      // Same calendar window as this year's YTD (Jan 1 → today's date),
      // shifted back 12 months. Lets you compare apples-to-apples year over year.
      const startThis = new Date(now.getFullYear(), 0, 1);
      const start = new Date(startThis);
      start.setFullYear(start.getFullYear() - 1);
      const end = new Date(today);
      end.setFullYear(end.getFullYear() - 1);
      end.setDate(end.getDate() + 1);
      return { start, end, label };
    }
  }
}

function toDateOnly(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Fetch per-user aggregates from crew_daily_points across an arbitrary
 * date window. Returns one row per user_id with counts summed.
 */
/**
 * Oct 27, 2026 (Greg): the Builds GOAL counts builds on the calendar for the
 * period — first build day inside it — not the crew `build` credit, which is
 * stamped from the preferred build date at midnight UTC and so drops a Monday
 * build into the week before. Goal surfaces call this; the leaderboard, wall
 * and points keep the credit.
 */
async function applyCalendarBuilds(
  stats: Record<string, WallStat>,
  fromISO: string,
  toISO: string,
): Promise<void> {
  const cal = await fetchCalendarBuilds(fromISO, toISO);
  for (const st of Object.values(stats)) st.builds = cal[st.user_id] ?? 0;
  for (const [uid, b] of Object.entries(cal)) {
    if (!stats[uid]) {
      stats[uid] = { user_id: uid, apps: 0, claims: 0, builds_scheduled: 0, builds: b, revenue_cents: 0 };
    }
  }
}

export async function fetchWallStats(
  range: WallRange,
  opts?: { buildsOnCalendar?: boolean },
): Promise<{ stats: Record<string, WallStat>; window: ReturnType<typeof rangeFor> }> {
  const window = rangeFor(range);
  const { data, error } = await supabase
    .from("crew_daily_points")
    .select("user_id, day, apps, claims, builds_scheduled, builds, revenue_cents")
    .gte("day", toDateOnly(window.start))
    .lt("day", toDateOnly(window.end));
  if (error) throw error;
  const out: Record<string, WallStat> = {};
  for (const r of data ?? []) {
    const uid = r.user_id as string;
    if (!out[uid]) {
      out[uid] = {
        user_id: uid,
        apps: 0,
        claims: 0,
        builds_scheduled: 0,
        builds: 0,
        revenue_cents: 0,
      };
    }
    const s = out[uid]!;
    s.apps += Number(r.apps ?? 0);
    s.claims += Number(r.claims ?? 0);
    s.builds_scheduled += Number(r.builds_scheduled ?? 0);
    s.builds += Number(r.builds ?? 0);
    s.revenue_cents += Number(r.revenue_cents ?? 0);
  }
  if (opts?.buildsOnCalendar) {
    await applyCalendarBuilds(out, toDateOnly(window.start), toDateOnly(window.end));
  }
  return { stats: out, window };
}

// ───────────────────── Canvasser funnel (Ship 4) ─────────────────────
// Canvassers are NOT in crew_daily_points / crew_leaderboard (apps there are
// credited to the RM owner). The canvasser_funnel RPC counts a canvasser's OWN
// approved apps (pending_canvas_appointments.submitted_by) and walks each app's
// deal through the pipeline. Spec: memory/features/canvasser-program.md.
export interface CanvasserFunnelRow {
  canvasser_id: string;
  full_name: string;
  apps: number;
  claims: number;
  builds_scheduled: number;
  builds: number;
  closed_won: number;
}

/** Active canvassers (role='canvasser'). Used by GoalsAdmin (apps-only goals). */
export async function fetchCanvassers(): Promise<Array<{ user_id: string; full_name: string }>> {
  const { data, error } = await supabase
    .from("team_roster")
    .select("id, full_name")
    .eq("role", "canvasser")
    .eq("is_active", true)
    .eq("on_leave", false)
    .order("full_name");
  if (error) throw error;
  return (data ?? []).map((r) => ({ user_id: r.id as string, full_name: r.full_name as string }));
}

export async function fetchCanvasserFunnel(range: WallRange): Promise<CanvasserFunnelRow[]> {
  const { start, end } = rangeFor(range);
  const { data, error } = await supabase.rpc("canvasser_funnel", {
    p_start: start.toISOString(),
    p_end: end.toISOString(),
  });
  if (error) throw error;
  return ((data ?? []) as CanvasserFunnelRow[]).map((r) => ({
    canvasser_id: r.canvasser_id,
    full_name: r.full_name,
    apps: Number(r.apps ?? 0),
    claims: Number(r.claims ?? 0),
    builds_scheduled: Number(r.builds_scheduled ?? 0),
    builds: Number(r.builds ?? 0),
    closed_won: Number(r.closed_won ?? 0),
  }));
}

// ───────────────────── Goals Audit (manager tab) ─────────────────────
/**
 * Range identifiers for the Goals Audit tab. Distinct from WallRange — the
 * audit needs trailing windows (30/90/180 days) plus a calendar year-to-date
 * view, and it overlays summed weekly goals on top of actuals.
 *
 * Data reality (Jun 2026): weekly goals only exist from 2026-04-27 onward, and
 * the only metrics with goals are apps/claims/builds_scheduled/builds. Ranges
 * before the first goal week render actual-only with a "no goal set" note.
 */
export type AuditRange =
  | "week"
  | "last_week"
  | "month"
  | "last_30"
  | "last_90"
  | "last_6mo"
  | "this_year";

export const AUDIT_RANGE_LABELS: Record<AuditRange, string> = {
  week: "This week",
  last_week: "Last week",
  month: "This month",
  last_30: "Last 30 days",
  last_90: "Last 90 days",
  last_6mo: "Last 6 months",
  this_year: "This year",
};

export const AUDIT_RANGE_ORDER: AuditRange[] = [
  "week",
  "last_week",
  "month",
  "last_30",
  "last_90",
  "last_6mo",
  "this_year",
];

/** Resolve an audit range to a half-open [start, end) window (end exclusive). */
export function auditRangeFor(
  r: AuditRange,
  now: Date = new Date(),
): { start: Date; end: Date; label: string } {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const label = AUDIT_RANGE_LABELS[r];
  switch (r) {
    case "week": {
      const mon = new Date(today);
      const day = mon.getDay(); // 0=Sun..6=Sat
      const diff = day === 0 ? -6 : 1 - day;
      mon.setDate(mon.getDate() + diff);
      return { start: mon, end: tomorrow, label };
    }
    case "last_week": {
      // Full prior Mon→Sun week (end exclusive = this week's Monday).
      const thisMon = new Date(today);
      const day = thisMon.getDay(); // 0=Sun..6=Sat
      const diff = day === 0 ? -6 : 1 - day;
      thisMon.setDate(thisMon.getDate() + diff);
      const lastMon = new Date(thisMon);
      lastMon.setDate(lastMon.getDate() - 7);
      return { start: lastMon, end: thisMon, label };
    }
    case "month":
      return {
        start: new Date(today.getFullYear(), today.getMonth(), 1),
        end: tomorrow,
        label,
      };
    case "last_30": {
      const s = new Date(today);
      s.setDate(s.getDate() - 30);
      return { start: s, end: tomorrow, label };
    }
    case "last_90": {
      const s = new Date(today);
      s.setDate(s.getDate() - 90);
      return { start: s, end: tomorrow, label };
    }
    case "last_6mo": {
      const s = new Date(today);
      s.setMonth(s.getMonth() - 6);
      return { start: s, end: tomorrow, label };
    }
    case "this_year":
      return { start: new Date(today.getFullYear(), 0, 1), end: tomorrow, label };
  }
}

/**
 * Per-user actuals over an audit window. Paginated via .range() so it can't be
 * silently truncated by PostgREST's ~1000-row cap if crew_daily_points grows.
 */
export async function fetchAuditActuals(
  range: AuditRange,
): Promise<{
  stats: Record<string, WallStat>;
  window: ReturnType<typeof auditRangeFor>;
}> {
  const window = auditRangeFor(range);
  const startStr = toDateOnly(window.start);
  const endStr = toDateOnly(window.end);
  const out: Record<string, WallStat> = {};
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("crew_daily_points")
      .select("user_id, apps, claims, builds_scheduled, builds, revenue_cents")
      .gte("day", startStr)
      .lt("day", endStr)
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = data ?? [];
    for (const r of rows) {
      const uid = r.user_id as string;
      if (!out[uid]) {
        out[uid] = {
          user_id: uid,
          apps: 0,
          claims: 0,
          builds_scheduled: 0,
          builds: 0,
          revenue_cents: 0,
        };
      }
      const s = out[uid]!;
      s.apps += Number(r.apps ?? 0);
      s.claims += Number(r.claims ?? 0);
      s.builds_scheduled += Number(r.builds_scheduled ?? 0);
      s.builds += Number(r.builds ?? 0);
      s.revenue_cents += Number(r.revenue_cents ?? 0);
    }
    if (rows.length < PAGE) break;
  }
  // Goal attainment: Builds = builds on the calendar in the range (Oct 27, 2026).
  await applyCalendarBuilds(out, startStr, endStr);
  return { stats: out, window };
}

/**
 * Weekly goals summed across every week-Monday that falls inside [start,end).
 * "Sum overlapping weekly goals" — a 30-day window picks up ~4 weekly targets.
 */
export interface AuditGoals {
  /** Per-user summed personal weekly targets within the range. */
  personalByUser: Record<string, Partial<Record<GoalMetricId, number>>>;
  /** Summed company-scope weekly targets within the range. */
  company: Partial<Record<GoalMetricId, number>>;
  /** Count of Monday-starts that fall in the range. */
  weeksInRange: number;
  /** Distinct goal weeks (any metric) actually found in the range. */
  goalWeeksFound: number;
  /** Earliest goal week ever recorded — drives the coverage note. null = none. */
  earliestGoalWeekEver: string | null;
}

export async function fetchAuditGoals(range: AuditRange): Promise<AuditGoals> {
  const { start, end } = auditRangeFor(range);
  const startStr = toDateOnly(start);
  const endStr = toDateOnly(end);
  const [inRange, earliest] = await Promise.all([
    supabase
      .from("crew_goals")
      .select("scope, user_id, metric, target, period_start")
      .eq("period", "week")
      .in("metric", WEEKLY_GOAL_METRICS)
      .gte("period_start", startStr)
      .lt("period_start", endStr),
    supabase
      .from("crew_goals")
      .select("period_start")
      .eq("period", "week")
      .order("period_start", { ascending: true })
      .limit(1),
  ]);
  if (inRange.error) throw inRange.error;
  if (earliest.error) throw earliest.error;

  const personalByUser: Record<string, Partial<Record<GoalMetricId, number>>> = {};
  const company: Partial<Record<GoalMetricId, number>> = {};
  const goalWeeks = new Set<string>();
  for (const r of inRange.data ?? []) {
    const metric = r.metric as GoalMetricId;
    const tgt = Number(r.target ?? 0);
    goalWeeks.add(r.period_start as string);
    if ((r.scope as string) === "company") {
      company[metric] = (company[metric] ?? 0) + tgt;
    } else if (r.user_id) {
      const uid = r.user_id as string;
      if (!personalByUser[uid]) personalByUser[uid] = {};
      personalByUser[uid]![metric] = (personalByUser[uid]![metric] ?? 0) + tgt;
    }
  }

  // Count Monday-starts within [start, end).
  let weeksInRange = 0;
  const cur = new Date(start);
  const dow = cur.getDay(); // advance to first Monday >= start
  const adv = dow === 0 ? 1 : dow === 1 ? 0 : 8 - dow;
  cur.setDate(cur.getDate() + adv);
  while (cur < end) {
    weeksInRange++;
    cur.setDate(cur.getDate() + 7);
  }

  const earliestGoalWeekEver =
    ((earliest.data?.[0]?.period_start as string | undefined) ?? null) || null;

  return {
    personalByUser,
    company,
    weeksInRange,
    goalWeeksFound: goalWeeks.size,
    earliestGoalWeekEver,
  };
}

/**
 * Per-user SPIFF dollars (cents) awarded within an audit window. Sources from
 * `crew_spiff_feed` (already scoped to active ASM/TL/RM — the same role set the
 * Goals Audit shows), summed by recipient over [start, end) on `claimed_at`.
 * Lets the audit answer "who won the weekly spiffs" across any range filter.
 */
export async function fetchAuditSpiffs(
  range: AuditRange,
): Promise<{ byUser: Record<string, number>; total: number }> {
  const { start, end } = auditRangeFor(range);
  const { data, error } = await supabase
    .from("crew_spiff_feed")
    .select("user_id, amount_cents, claimed_at")
    .gte("claimed_at", start.toISOString())
    .lt("claimed_at", end.toISOString());
  if (error) throw error;
  const byUser: Record<string, number> = {};
  let total = 0;
  for (const r of data ?? []) {
    const uid = r.user_id as string;
    const cents = Number(r.amount_cents ?? 0);
    byUser[uid] = (byUser[uid] ?? 0) + cents;
    total += cents;
  }
  return { byUser, total };
}

/** One SPIFF line for the audit "Spiffs won" breakdown. */
export interface AuditSpiffLine {
  id: string;
  user_id: string;
  user_name: string;
  name: string;
  amount_cents: number;
  claimed_at: string;
}

/**
 * Detailed SPIFF lines within an audit window — every individual SPIFF won, so
 * the Goals Audit "Spiffs won" panel can be reviewed rep-by-rep through the
 * week. Sources from `crew_spiff_feed` (RM/TL/ASM, ASM-override rows excluded),
 * newest first.
 */
export async function fetchAuditSpiffDetail(
  range: AuditRange,
): Promise<AuditSpiffLine[]> {
  const { start, end } = auditRangeFor(range);
  const { data, error } = await supabase
    .from("crew_spiff_feed")
    .select("id, user_id, user_name, name, amount_cents, claimed_at")
    .gte("claimed_at", start.toISOString())
    .lt("claimed_at", end.toISOString())
    .order("claimed_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id as string,
    user_id: r.user_id as string,
    user_name: (r.user_name as string) ?? "—",
    name: (r.name as string) ?? "SPIFF",
    amount_cents: Number(r.amount_cents ?? 0),
    claimed_at: r.claimed_at as string,
  }));
}

/**
 * Active reps eligible to receive a weekly SPIFF — RM + TL only, matching the
 * Weekly SPIFF program (Monday Madness / Terrific Tuesday / Hump Day /
 * Perfect Week excludes ASM,
 * per WeeklySpiffsCard / SpifsTab). `asm_id` is returned so the Give-a-SPIFF
 * modal can pay the rep's ASM a 50% override on every awarded SPIFF.
 */
export async function fetchSpiffRecipients(): Promise<
  Array<{ id: string; full_name: string; role: string; asm_id: string | null }>
> {
  const { data, error } = await supabase
    .from("team_roster")
    .select("id, full_name, role, asm_id")
    .eq("is_active", true)
    .eq("on_leave", false)
    .in("role", ["team_lead", "restore_manager"])
    .order("full_name");
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id as string,
    full_name: r.full_name as string,
    role: r.role as string,
    asm_id: (r.asm_id as string | null) ?? null,
  }));
}

/**
 * Active ASMs — used to (a) name the override recipient in the Give-a-SPIFF
 * modal and (b) fall back to the sole active ASM when a rep's `asm_id` is unset.
 */
export async function fetchActiveAsms(): Promise<
  Array<{ id: string; full_name: string }>
> {
  const { data, error } = await supabase
    .from("team_roster")
    .select("id, full_name")
    .eq("is_active", true)
    .eq("role", "asm")
    .order("full_name");
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id as string,
    full_name: r.full_name as string,
  }));
}

/**
 * Record a manager-awarded cash SPIFF. `claimedAt` is an ISO timestamp — pass a
 * back-dated value to log a SPIFF from a prior week (it lands in that week's
 * audit bucket). RLS gates the insert to crew managers; `awardedBy` stamps the
 * actor for the audit trail.
 */
export async function awardSpiff(input: {
  userId: string;
  name: string;
  amountCents: number;
  claimedAt: string;
  jobAddress?: string | null;
  notes?: string | null;
  awardedBy: string;
}): Promise<void> {
  const { error } = await supabase.from("spiffs").insert({
    user_id: input.userId,
    name: input.name,
    amount_cents: input.amountCents,
    claimed_at: input.claimedAt,
    job_address: input.jobAddress ?? null,
    notes: input.notes ?? null,
    awarded_by: input.awardedBy,
    source: "manual",
  });
  if (error) throw error;
}

/**
 * Award one or more SPIFFs to a single rep in one shot, and pay that rep's ASM a
 * 50% override on the combined total.
 *
 * Writes one `spiffs` row per selected SPIFF (so each shows by name in the audit
 * + the weekly Shannon email), plus — when an ASM is resolved — a single
 * `source='asm_override'` row for 50% of the combined total, credited to the
 * ASM. All rows share the same `claimed_at` so they land in the same weekly
 * bucket the `spiff-weekly-summary` email reads from.
 *
 * The override row is rounded to the nearest cent. If `asmId` is null (rep has
 * no ASM and none could be resolved) the override is skipped and the caller is
 * told via the returned `asmPaidCents = 0`.
 */
export async function awardSpiffs(input: {
  userId: string;
  repName: string;
  spiffs: Array<{ name: string; amountCents: number }>;
  asmId: string | null;
  claimedAt: string;
  jobAddress?: string | null;
  notes?: string | null;
  awardedBy: string;
}): Promise<{ repPaidCents: number; asmPaidCents: number }> {
  const valid = input.spiffs.filter((s) => s.name.trim() && s.amountCents > 0);
  if (valid.length === 0) throw new Error("Pick at least one SPIFF with an amount.");

  const repTotal = valid.reduce((sum, s) => sum + s.amountCents, 0);
  const asmTotal = input.asmId ? Math.round(repTotal * 0.5) : 0;

  const rows: Array<{
    user_id: string;
    name: string;
    amount_cents: number;
    claimed_at: string;
    job_address: string | null;
    notes: string | null;
    awarded_by: string;
    source: string;
  }> = valid.map((s) => ({
    user_id: input.userId,
    name: s.name.trim(),
    amount_cents: s.amountCents,
    claimed_at: input.claimedAt,
    job_address: input.jobAddress ?? null,
    notes: input.notes ?? null,
    awarded_by: input.awardedBy,
    source: "manual",
  }));

  if (input.asmId && asmTotal > 0) {
    rows.push({
      user_id: input.asmId,
      name: `ASM Override (50%) — ${input.repName}`,
      amount_cents: asmTotal,
      claimed_at: input.claimedAt,
      job_address: input.jobAddress ?? null,
      notes: `ASM 50% override on ${input.repName}'s SPIFFs (${fmtUSD(
        repTotal / 100,
      )} total)${input.notes ? ` — ${input.notes}` : ""}`,
      awarded_by: input.awardedBy,
      source: "asm_override",
    });
  }

  const { error } = await supabase.from("spiffs").insert(rows);
  if (error) throw error;
  return { repPaidCents: repTotal, asmPaidCents: asmTotal };
}

/** One SPIFF row as the Finance Board pay tracker sees it (rep + ASM override). */
export interface FinanceSpiffRow {
  id: string;
  user_id: string;
  recipient_name: string;
  recipient_role: string;
  name: string;
  amount_cents: number;
  job_address: string | null;
  claimed_at: string;
  source: string;
  paid_at: string | null;
  paid_by: string | null;
  /** Oct 8, 2026 — the Books bill this SPIFF was sent on (spiffs_send_to_books); null = not sent. */
  books_bill_id: string | null;
  /** Oct 27, 2026 — when Alex (or Chad / Greg) approved it; null = waiting (20261027j_spiff_approval.sql). */
  approved_at: string | null;
}

/**
 * Every SPIFF for the Finance Board pay tracker — rep SPIFFs AND ASM 50%
 * overrides, with no manual/automatic distinction. Newest first.
 *
 * `sinceDate` (YYYY-MM-DD) bounds the window so paid history doesn't grow
 * unbounded. Crucially, **unpaid SPIFFs are ALWAYS returned regardless of age**
 * — a pay tracker must never hide money still owed. Pass null for no bound.
 */
export async function fetchFinanceSpiffs(
  sinceDate?: string | null,
): Promise<FinanceSpiffRow[]> {
  let q = supabase
    .from("spiffs")
    .select(
      "id, user_id, name, amount_cents, job_address, claimed_at, source, paid_at, paid_by, books_bill_id, approved_at, recipient:team_roster!spiffs_user_id_fkey(full_name, role)",
    )
    .order("claimed_at", { ascending: false });
  if (sinceDate) {
    // In-window OR still-unpaid (so nothing owed is ever hidden by the window).
    q = q.or(`claimed_at.gte.${sinceDate},paid_at.is.null`);
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map((r) => {
    const rec = (r as { recipient?: { full_name?: string; role?: string } | { full_name?: string; role?: string }[] }).recipient;
    const recObj = Array.isArray(rec) ? rec[0] : rec;
    return {
      id: r.id as string,
      user_id: r.user_id as string,
      recipient_name: recObj?.full_name ?? "—",
      recipient_role: recObj?.role ?? "",
      name: r.name as string,
      amount_cents: Number(r.amount_cents ?? 0),
      job_address: (r.job_address as string | null) ?? null,
      claimed_at: r.claimed_at as string,
      source: (r.source as string) ?? "manual",
      paid_at: (r.paid_at as string | null) ?? null,
      paid_by: (r.paid_by as string | null) ?? null,
      books_bill_id: (r.books_bill_id as string | null) ?? null,
      approved_at: (r.approved_at as string | null) ?? null,
    };
  });
}

/** A SPIFF row for Chad's audit board — adds the awarder for manual awards. */
export interface SpiffAuditRow extends FinanceSpiffRow {
  awarded_by: string | null;
}

/**
 * Every SPIFF whose `claimed_at` falls in [fromDate, beforeDate) — ET, upper
 * bound EXCLUSIVE, so callers with an inclusive end date pass end + 1 day.
 *
 * Deliberately unlike `fetchFinanceSpiffs`, which also returns out-of-window
 * unpaid rows so a pay tracker never hides money owed. That behaviour would
 * corrupt an audit stat — "top earner over the last 30 days" must not silently
 * include a stale unpaid row from March.
 *
 * The -04:00 offset matches the convention already used by fetchFinanceSpiffs
 * and the spiff-weekly-summary edge function (EDT). Off by an hour at the
 * boundary during EST; immaterial for a range that spans days or months.
 */
export async function fetchSpiffAudit(
  fromDate: string,
  beforeDate: string,
): Promise<SpiffAuditRow[]> {
  const { data, error } = await supabase
    .from("spiffs")
    .select(
      "id, user_id, name, amount_cents, job_address, claimed_at, source, paid_at, paid_by, books_bill_id, approved_at, awarded_by, recipient:team_roster!spiffs_user_id_fkey(full_name, role)",
    )
    .gte("claimed_at", `${fromDate}T00:00:00-04:00`)
    .lt("claimed_at", `${beforeDate}T00:00:00-04:00`)
    .order("claimed_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => {
    const rec = (r as { recipient?: { full_name?: string; role?: string } | { full_name?: string; role?: string }[] }).recipient;
    const recObj = Array.isArray(rec) ? rec[0] : rec;
    return {
      id: r.id as string,
      user_id: r.user_id as string,
      recipient_name: recObj?.full_name ?? "—",
      recipient_role: recObj?.role ?? "",
      name: r.name as string,
      amount_cents: Number(r.amount_cents ?? 0),
      job_address: (r.job_address as string | null) ?? null,
      claimed_at: r.claimed_at as string,
      source: (r.source as string) ?? "manual",
      paid_at: (r.paid_at as string | null) ?? null,
      paid_by: (r.paid_by as string | null) ?? null,
      books_bill_id: (r.books_bill_id as string | null) ?? null,
      approved_at: (r.approved_at as string | null) ?? null,
      awarded_by: (r.awarded_by as string | null) ?? null,
    };
  });
}

/**
 * Verified 5-star reviews on ET dates from..to (inclusive) from the
 * five_star_review_credits() RPC — security definer, because an RM's deals RLS
 * can't see teammates' reviews. Every review comes back with each rep's credit
 * (recognition is public); `amount_cents` / `reason` are filled only for reps
 * whose pay the viewer may see, null otherwise. Throws on a failed read.
 */
export async function fetchReviewCredits(fromDate: string, toDate: string): Promise<ReviewCreditRow[]> {
  const { data, error } = await supabase.rpc("five_star_review_credits", {
    p_from: fromDate,
    p_to: toDate,
  });
  if (error) throw error;
  // numeric columns can arrive as strings through PostgREST — normalize.
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    deal_id: String(r.deal_id),
    user_id: String(r.user_id),
    share: Number(r.share ?? 0),
    verified_at: String(r.verified_at ?? ""),
    verified_day: String(r.verified_day ?? ""),
    amount_cents: r.amount_cents === null || r.amount_cents === undefined ? null : Number(r.amount_cents),
    reason: r.reason === null || r.reason === undefined ? null : String(r.reason),
  }));
}

/**
 * Crew activity + eligible reps for one SPIFF week (Mon..Sat), the shared feed
 * behind the home spiff strip, the Monday recognition and the crew board.
 * Eligibility is active RM + TL — the ASM earns a 50% override, not the base
 * SPIFF.
 *
 * Oct 19, 2026: also the week's 5-Star Review credits — fetchReviewCredits()
 * from the Monday through the next Monday, because a review verified before
 * noon that next Monday still belongs to this week (computeSpiffSummary keeps
 * only this week's). That read is an add-on: if it fails the four-spiff race
 * still renders and `reviews` is null, so the review tile says it couldn't
 * load instead of claiming nobody earned one.
 */
export async function fetchWeeklySpiffData(weekMonday: string): Promise<{
  activity: SpiffActivityRow[];
  members: Array<{ id: string; full_name: string | null }>;
  reviews: ReviewCreditRow[] | null;
}> {
  const saturday = addDaysISO(weekMonday, 5);
  const reviewDays = reviewCreditDays(weekMonday);
  const [{ data: a, error: e1 }, { data: m, error: e2 }, rv] = await Promise.all([
    supabase
      .from("crew_daily_points")
      .select("user_id,day,apps,claims,builds_scheduled,builds")
      .gte("day", weekMonday)
      .lte("day", saturday),
    supabase
      .from("team_roster")
      .select("id, full_name")
      .eq("is_active", true)
      .in("role", ["restore_manager", "team_lead"])
      .order("full_name"),
    fetchReviewCredits(reviewDays.from, reviewDays.to).then(
      (rows) => ({ rows, failed: false as const }),
      (err: unknown) => {
        console.warn("five_star_review_credits failed", err);
        return { rows: null, failed: true as const };
      },
    ),
  ]);
  const error = e1 ?? e2;
  if (error) throw error;
  const reviews: ReviewCreditRow[] | null = rv.failed ? null : rv.rows;
  return {
    activity: (a ?? []) as SpiffActivityRow[],
    members: (m ?? []) as Array<{ id: string; full_name: string | null }>,
    reviews,
  };
}

/**
 * Mark SPIFF rows paid or unpaid. RLS gates UPDATE to finance + crew managers.
 */
export async function setSpiffsPaid(
  ids: string[],
  paid: boolean,
  paidBy: string,
): Promise<void> {
  if (ids.length === 0) return;
  const patch = paid
    ? { paid_at: new Date().toISOString(), paid_by: paidBy }
    : { paid_at: null, paid_by: null };
  const { error } = await supabase.from("spiffs").update(patch).in("id", ids);
  if (error) throw error;
}

// ── Monday SPIFF approval (Oct 27, 2026; 20261027j_spiff_approval.sql) ─────────
// Alex (the ASM) approves last week's SPIFFs on the ASM board; Chad or Greg when he
// hasn't. Approving makes one Books bill per person; Shannon pays them in Books.

/** Every SPIFF waiting for approval, and those approved in the last `days` days (approvers only; the RPC refuses others). */
export async function fetchSpiffsForApproval(days = 14): Promise<ApprovalSpiff[]> {
  const { data, error } = await supabase.rpc("spiffs_for_approval", { p_days: days });
  if (error) throw error;
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    id: String(r.id),
    user_id: String(r.user_id),
    recipient_name: (r.recipient_name as string) ?? "—",
    name: (r.name as string) ?? "",
    amount_cents: Number(r.amount_cents ?? 0),
    claimed_at: String(r.claimed_at),
    source: (r.source as string) ?? "manual",
    job_address: (r.job_address as string | null) ?? null,
    approved_at: (r.approved_at as string | null) ?? null,
    approved_by_name: (r.approved_by_name as string | null) ?? null,
    paid_at: (r.paid_at as string | null) ?? null,
    books_bill_id: (r.books_bill_id as string | null) ?? null,
  }));
}

/** Approve SPIFFs: marks them approved and makes their Books bills in one call. */
export async function approveSpiffs(ids: string[]): Promise<{ approved: number; totalCents: number }> {
  const { data, error } = await supabase.rpc("spiffs_approve", { p_ids: ids });
  if (error) throw error;
  const out = (data ?? {}) as { approved_ids?: string[]; total_cents?: number };
  return { approved: out.approved_ids?.length ?? 0, totalCents: Number(out.total_cents ?? 0) };
}

/** Remove a SPIFF that is still waiting for approval (audited with the reason). */
export async function removeSpiff(id: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc("spiff_remove", { p_id: id, p_reason: reason });
  if (error) throw error;
}

/** How many SPIFFs wait for approval (0 for anybody who does not approve). */
export async function fetchSpiffsWaitingCount(): Promise<number> {
  const { data, error } = await supabase.rpc("spiffs_waiting_count");
  if (error) throw error;
  return Number(data ?? 0);
}
